import { GridApiClient } from '../api/grid-api-client';
import {
  AuthorizationError,
  InitAuthResponse,
  CompleteAuthResponse,
  UniversalKeyPair,
  SessionSecrets,
  CompleteAuthRequestWithOtp,
  CompleteAuthAndCreateAccountRequest,
  ProviderError,
  SignerProvider,
  TaggedKeyPair,
  KeyPairTag,
  CompleteAuthAndCreateAccountResponse,
} from '../types';
import { PrivyInitAuthRequest, PrivyCompleteAuthRequest } from '../types/privy';
import { TurnkeyInitAuthRequest, TurnkeyCompleteAuthRequest } from '../types/turnkey';
import { generateHPKEKeyPair, generateTurnkeyKeyPair } from '@sqds/grid-crypto';
import { generateSolanaKeyPair } from '../utils/crypto/solana-crypto';
import { bytesToHex } from '../utils/encoding';

/**
 * Authentication request parameters for various providers.
 * @property {string} [email] - User email for email-based authentication
 * @property {UniversalKeyPair} [keypair] - Key pair for cryptographic authentication
 * @property {string} [passkeyId] - Passkey identifier for passkey authentication
 * @property {SignerProvider} [provider] - Authentication provider type
 * @property {number} [expiration] - Authentication expiration timestamp
 */
export interface AuthenticationRequest {
  email?: string;
  keypair?: UniversalKeyPair;
  passkeyId?: string;
  provider?: SignerProvider;
  expiration?: number;
  smartAccountAddress?: string;
}

/**
 * @hidden
 */
export interface AuthenticationContext {
  provider: SignerProvider;
  sessionId?: string;
  authPublicKey?: string;
  email?: string;
  keypair?: UniversalKeyPair;
}

/**
 * @hidden
 */
export class AuthenticationManager {
  private gridApiClient: GridApiClient;
  private currentContext?: AuthenticationContext;

  constructor(apiClient: GridApiClient) {
    this.gridApiClient = apiClient;
  }

  /**
   * Smart authentication routing based on request type
   */
  async authenticate(request: AuthenticationRequest): Promise<InitAuthResponse> {
    try {
      // Determine authentication type
      const authType = this.determineAuthType(request);
      
      switch (authType) {
        case 'privy':
        case 'turnkey':
          return  await this.initiateEmailAuth(request.email!, authType, request.expiration, request.smartAccountAddress)
        
        case 'solana':
          return await this.validateEOAWallet(request.keypair!)
        
        case 'passkey':
          return await this.initiatePasskeyAuth(request.passkeyId!)
        
        default:
          throw new AuthorizationError(`Unsupported authentication type: ${authType}`, 'UNSUPPORTED_AUTH_TYPE');
      }
    } catch (error: any) {
      if (error instanceof AuthorizationError) throw error;
      throw new AuthorizationError(
        error.message || "Authentication failed",
        'AUTHENTICATION_FAILED',
        undefined,
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Complete authentication based on provider type
   */
  async complete(request: CompleteAuthRequestWithOtp | CompleteAuthAndCreateAccountRequest): Promise<CompleteAuthResponse> {
    const { user } = request as CompleteAuthAndCreateAccountRequest;

    // Simplified Privy-only implementation for now
    if (user.provider === 'privy') {
      return await this.completePrivyAuth(request);
    } else if (user.provider === 'turnkey') {

      return await this.completeTurnkeyAuth(request, user.otpId);
    } else {
      throw new AuthorizationError('Unsupported provider', 'UNSUPPORTED_PROVIDER');
    }
  }

  /**
   * Complete authentication and create account
   */
  async completeAndCreateAccount(request: CompleteAuthAndCreateAccountRequest): Promise<CompleteAuthAndCreateAccountResponse> {
    // Simplified Privy-only implementation for now
    return this.completePrivyAuthAndCreateAccount(request);
  }

  /**
   * Generate keypairs for all supported providers with appropriate tagging
   * Returns an array of tagged keypairs for privy (primary), turnkey (backup), solana, and passkey
   */
  async generateSessionSecrets(): Promise<SessionSecrets> {
    const providers: Array<{ type: SignerProvider, tag: KeyPairTag }> = [
      { type: 'privy', tag: 'primary' },
      { type: 'turnkey', tag: 'backup' },
      { type: 'solana', tag: 'solana' },
      { type: 'passkey', tag: 'passkey' }
    ];

    const keyPairs: TaggedKeyPair[] = [];

    for (const { type, tag } of providers) {
      try {
        const keyPair = await this.generateKeyPair(type);
        keyPairs.push({
          publicKey: keyPair.publicKey,
          privateKey: keyPair.privateKey,
          publicKeyUncompressed: keyPair.publicKeyUncompressed,
          provider: type,
          tag: tag
        });
      } catch (error: any) {
        console.warn(`⚠️ Failed to generate keypair with ${type} provider:`, error.message);
        // Continue with other providers instead of failing completely
      }
    }

    if (keyPairs.length === 0) {
      throw new ProviderError(
        'Failed to generate keypairs with any provider. Please check provider configurations.',
        'privy'
      );
    }

    return keyPairs;
  }

  /**
   * Generate keypair for specific provider
   */
  async generateKeyPair(provider: SignerProvider): Promise<UniversalKeyPair> {
    switch (provider) {
      case 'privy':
        // For now, use HPKE key pair generation
        const hpkeKeyPair = await generateHPKEKeyPair();
        return {
          publicKey: hpkeKeyPair.publicKey,
          privateKey: hpkeKeyPair.privateKey
        };
      
      case 'turnkey':
        return generateTurnkeyKeyPair();
      
      case 'solana':
        const solanaKp = await generateSolanaKeyPair();
        return {
          publicKey: solanaKp.publicKey.toString(),
          privateKey: bytesToHex(solanaKp.secretKey)
        };

      case 'passkey':
        const passkeyKp = await generateSolanaKeyPair();
        return {
          publicKey: passkeyKp.publicKey.toString(),
          privateKey: bytesToHex(passkeyKp.secretKey)
        };
      
      default:
        throw new AuthorizationError(
          `Keypair generation not supported for provider: ${provider}`,
          'UNSUPPORTED_PROVIDER'
        );
    }
  }

  // Private methods for each authentication type

  private async initiateEmailAuth(
    email: string,
    provider: 'privy' | 'turnkey',
    expiration?: number,
    smartAccountAddress?: string
  ): Promise<InitAuthResponse> {

    // Generate auth keypair for the provider
    const authKeyPair = await this.generateKeyPair(provider);

    // Store context
    this.currentContext = {
      provider,
      email,
      authPublicKey: authKeyPair.publicKey
    };

    // Call appropriate API method
    if (provider === 'privy') {
      const request: PrivyInitAuthRequest = {
        email,
        expiration,
        smartAccountAddress
      };
      const response = await this.gridApiClient.initAuthWithPrivy(request);

      return response;
    } else {
      const request: TurnkeyInitAuthRequest = {
        email,
        // expiration,
        provider,
        smartAccountAddress
      };
      const response = await this.gridApiClient.initAuthWithTurnkey(request);

      return response;
    }
  }

  private async validateEOAWallet(keypair: UniversalKeyPair): Promise<InitAuthResponse> {
    // For EOA wallets, we just validate the keypair
    if (!keypair.publicKey || !keypair.privateKey) {
      throw new AuthorizationError(
        'Invalid keypair: missing public or private key',
        'INVALID_KEYPAIR'
      );
    }

    // Store context
    this.currentContext = {
      provider: 'solana',
      keypair
    };

    // No actual authentication needed for EOA
    return {
      data: {
        authenticated: true,
        provider: 'solana',
        publicKey: keypair.publicKey
      }
    };
  }

  private async initiatePasskeyAuth(passkeyId: string): Promise<InitAuthResponse> {
    throw new AuthorizationError(
      'Passkey authentication not yet implemented',
      'NOT_IMPLEMENTED'
    );
  }

  private async completePrivyAuth(request: CompleteAuthRequestWithOtp | CompleteAuthAndCreateAccountRequest): Promise<any> {
    const { otpCode, user, sessionSecrets } = request as CompleteAuthAndCreateAccountRequest;
    
    // Extract email from context or user
    const email = user.email ?? this.currentContext?.email ;
    if (!email) {
      throw new AuthorizationError('Email is required for Privy authentication', 'MISSING_EMAIL');
    }

    // Find privy keypair
    const privyKeypair = sessionSecrets.find(kp => kp.provider === 'privy');
    if (!privyKeypair) {
      throw new AuthorizationError('Privy keypair not found', 'MISSING_KEYPAIR');
    }

    const smartAccountAddress = (request as CompleteAuthRequestWithOtp).smartAccountAddress;

    const privyRequest: PrivyCompleteAuthRequest = {
      email,
      otpCode,
      authPublicKey: privyKeypair.publicKey,
      smartAccountAddress
    };

    const response = await this.gridApiClient.completePrivyAuth(privyRequest);

    return response;
  }

  private async completeTurnkeyAuth(request: CompleteAuthRequestWithOtp | CompleteAuthAndCreateAccountRequest, otpId?: string): Promise<any> {
    const { otpCode, user, sessionSecrets } = request as CompleteAuthAndCreateAccountRequest;

    // Extract email from context or user
    const email = user.email ?? this.currentContext?.email;
    if (!email) {
      throw new AuthorizationError('Email is required for Turnkey authentication', 'MISSING_EMAIL');
    }

    // Validate required fields
    if (!otpId) {
      throw new AuthorizationError('OTP ID is required for Turnkey authentication', 'MISSING_OTP_ID');
    }

    // Find turnkey keypair
    const turnkeyKeypair = sessionSecrets.find(kp => kp.provider === 'turnkey');
    if (!turnkeyKeypair) {
      throw new AuthorizationError('Turnkey keypair not found', 'MISSING_KEYPAIR');
    }

    if (!turnkeyKeypair.publicKeyUncompressed) {
      throw new AuthorizationError('Turnkey public key uncompressed is required', 'MISSING_PUBLIC_KEY');
    }

    const smartAccountAddress = (request as CompleteAuthRequestWithOtp).smartAccountAddress;

    // TODO: These would need to come from the initAuth response in real implementation
    const turnkeyRequest: TurnkeyCompleteAuthRequest = {
      otpId: otpId,
      otpCode: otpCode,
      email: email,
      authPublicKey: turnkeyKeypair.publicKeyUncompressed,
      smartAccountAddress
    };

    const response = await this.gridApiClient.completeTurnkeyAuth(turnkeyRequest);

    return response;
  }

  private async completePrivyAuthAndCreateAccount(request: CompleteAuthAndCreateAccountRequest): Promise<CompleteAuthAndCreateAccountResponse> {
    const { otpCode, user, sessionSecrets } = request;

    const email = user.email ?? this.currentContext?.email;
    if (!email) {
      throw new AuthorizationError('Email is required', 'MISSING_EMAIL');
    }

    const privyKeypair = sessionSecrets.find(kp => kp.provider === 'privy');
    if (!privyKeypair) {
      throw new AuthorizationError('Privy keypair not found', 'MISSING_KEYPAIR');
    }

    const privyRequest: PrivyCompleteAuthRequest = {
      email,
      otpCode,
      authPublicKey: privyKeypair.publicKey
    };

    const response = await this.gridApiClient.completePrivyAuthAndCreateAccount(privyRequest);

    return response;
  }

  private determineAuthType(request: AuthenticationRequest): SignerProvider {
    // Explicit provider
    if (request.provider) {
      return request.provider;
    }

    // Email-based auth (default to privy)
    if (request.email) {
      return 'privy';
    }

    // EOA wallet
    if (request.keypair) {
      return 'solana';
    }

    // Passkey
    if (request.passkeyId) {
      return 'passkey';
    }

    throw new AuthorizationError(
      'Unable to determine authentication type from request',
      'AMBIGUOUS_AUTH_REQUEST'
    );
  }
  // Getters for current context
  getCurrentProvider(): SignerProvider | undefined {
    return this.currentContext?.provider;
  }

  getCurrentContext(): AuthenticationContext | undefined {
    return this.currentContext;
  }

  clearContext(): void {
    this.currentContext = undefined;
  }
}